import { useEffect, useCallback, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Zap, Radio, MapPin, Clock, RefreshCw, Globe2 } from "lucide-react";
import DispatchMetricsPanel from "./DispatchMetricsPanel";
import DispatchAdvisorPanel from "./DispatchAdvisorPanel";
import DispatchEfficiencyPanel from "./DispatchEfficiencyPanel";
import NearbyDriversPanel from "./NearbyDriversPanel";

const TIER_COLORS = {
  priority: "bg-red-100 text-red-700 border-red-300",
  regional: "bg-amber-100 text-amber-700 border-amber-300",
  national: "bg-blue-100 text-blue-700 border-blue-300",
};

const STATUS_COLORS = {
  available: "bg-green-500",
  returning_empty: "bg-cyan-500",
  online: "bg-blue-500",
  scheduled: "bg-purple-500",
  busy: "bg-amber-500",
  offline: "bg-slate-400",
};

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.round((d - new Date()) / 60000);
  if (Math.abs(mins) < 60) return mins > 0 ? `in ${mins}m` : `${-mins}m ago`;
  return d.toLocaleTimeString();
}

export default function DispatchTab({ dispatchData, setDispatchData, auth, API, actionLoading, setActionLoading }) {
  const [settingsForm, setSettingsForm] = useState({});
  const [showSettings, setShowSettings] = useState(false);

  const fetchDispatch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/dispatch`, auth());
      setDispatchData(res.data);
      setSettingsForm(res.data.settings || {});
    } catch (e) {
      console.error(e);
    }
  }, [API, auth, setDispatchData]);

  useEffect(() => {
    fetchDispatch();
    const t = setInterval(fetchDispatch, 15000);
    return () => clearInterval(t);
  }, [fetchDispatch]);

  const releaseNationally = async (bookingId) => {
    if (!window.confirm("Skip priority/regional tiers and release this booking to all drivers nationally?")) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/release-nationally`, {}, auth());
      toast.success("Booking released nationally");
      fetchDispatch();
    } catch (e) {
      toast.error("Release failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setActionLoading(false);
    }
  };

  const saveSettings = async () => {
    setActionLoading(true);
    try {
      const payload = {
        priority_radius_miles: parseFloat(settingsForm.priority_radius_miles),
        regional_radius_miles: parseFloat(settingsForm.regional_radius_miles),
        priority_duration_seconds: parseInt(settingsForm.priority_duration_seconds),
        regional_duration_seconds: parseInt(settingsForm.regional_duration_seconds),
      };
      await axios.post(`${API}/admin/dispatch/settings`, payload, auth());
      toast.success("Dispatch settings saved");
      setShowSettings(false);
      fetchDispatch();
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setActionLoading(false);
    }
  };

  if (!dispatchData) {
    return <div className="text-center py-12 text-slate-400" data-testid="dispatch-loading">Loading dispatch data…</div>;
  }

  const { in_flight = [], driver_status_breakdown = {}, total_online_drivers = 0, settings = {} } = dispatchData;
  const priCount = in_flight.filter(b => b.dispatch_state === "priority").length;
  const regCount = in_flight.filter(b => b.dispatch_state === "regional").length;
  const natCount = in_flight.filter(b => b.dispatch_state === "national").length;

  return (
    <div className="space-y-4" data-testid="dispatch-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Zap className="h-5 w-5 text-orange-500" /> Intelligent Dispatch
          </h2>
          <p className="text-xs text-slate-500">Live priority dispatch monitor — bookings advance every 30 s</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchDispatch} disabled={actionLoading} data-testid="dispatch-refresh">
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowSettings(!showSettings)} data-testid="dispatch-settings-toggle">
            Settings
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Priority tier</p><p className="text-2xl font-bold text-red-600" data-testid="dispatch-count-priority">{priCount}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Regional tier</p><p className="text-2xl font-bold text-amber-600" data-testid="dispatch-count-regional">{regCount}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">National tier</p><p className="text-2xl font-bold text-blue-600" data-testid="dispatch-count-national">{natCount}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Online drivers</p><p className="text-2xl font-bold text-green-600" data-testid="dispatch-online-drivers">{total_online_drivers}</p></CardContent></Card>
      </div>

      {/* Status breakdown chips */}
      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(driver_status_breakdown).map(([s, n]) => (
          <Badge key={s} className="bg-slate-100 text-slate-700 border-slate-200 flex items-center gap-1.5" data-testid={`dispatch-status-chip-${s}`}>
            <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[s] || "bg-slate-400"}`} />
            {s.replace("_", " ")}: {n}
          </Badge>
        ))}
      </div>

      {/* Performance metrics panel (Phase 7) */}
      <DispatchMetricsPanel auth={auth} API={API} />

      {/* Read-only dispatch advisor (Phase 8) */}
      <DispatchAdvisorPanel auth={auth} API={API} />

      {/* Phase 11 — Fleet efficiency & deadhead (read-only) */}
      <DispatchEfficiencyPanel auth={auth} API={API} />

      {showSettings && (
        <Card data-testid="dispatch-settings-panel">
          <CardHeader className="pb-2"><CardTitle className="text-base">Dispatch tunables</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-600">Priority radius (mi)</label><Input type="number" step="1" value={settingsForm.priority_radius_miles ?? ""} onChange={e => setSettingsForm({ ...settingsForm, priority_radius_miles: e.target.value })} data-testid="settings-priority-radius" /></div>
              <div><label className="text-xs text-slate-600">Regional radius (mi)</label><Input type="number" step="5" value={settingsForm.regional_radius_miles ?? ""} onChange={e => setSettingsForm({ ...settingsForm, regional_radius_miles: e.target.value })} data-testid="settings-regional-radius" /></div>
              <div><label className="text-xs text-slate-600">Priority duration (sec)</label><Input type="number" step="30" value={settingsForm.priority_duration_seconds ?? ""} onChange={e => setSettingsForm({ ...settingsForm, priority_duration_seconds: e.target.value })} data-testid="settings-priority-duration" /></div>
              <div><label className="text-xs text-slate-600">Regional duration (sec)</label><Input type="number" step="30" value={settingsForm.regional_duration_seconds ?? ""} onChange={e => setSettingsForm({ ...settingsForm, regional_duration_seconds: e.target.value })} data-testid="settings-regional-duration" /></div>
            </div>
            <Button size="sm" onClick={saveSettings} disabled={actionLoading} className="bg-orange-500 hover:bg-orange-600" data-testid="dispatch-settings-save">Save</Button>
          </CardContent>
        </Card>
      )}

      {/* In-flight bookings */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Radio className="h-4 w-4" /> In-flight bookings ({in_flight.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {in_flight.length === 0 ? (
            <p className="text-center text-slate-400 py-6 text-sm">No bookings currently being dispatched.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Pickup</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead className="text-center">Candidates</TableHead>
                  <TableHead>Next transition</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {in_flight.map(b => {
                  const nextT = b.dispatch_state === "priority" ? b.priority_until : b.dispatch_state === "regional" ? b.regional_until : null;
                  return (
                    <TableRow key={b.booking_id} data-testid={`dispatch-row-${b.ref}`}>
                      <TableCell className="font-mono text-xs">{b.ref}</TableCell>
                      <TableCell><Badge className={TIER_COLORS[b.dispatch_state] + " border"} data-testid={`dispatch-tier-${b.ref}`}>{b.dispatch_state}</Badge></TableCell>
                      <TableCell className="text-xs"><span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-slate-400" />{b.pickup_postcode || "?"}</span></TableCell>
                      <TableCell className="text-xs capitalize">{(b.vehicle_type || "").replace("_", " ")}</TableCell>
                      <TableCell className="text-right font-semibold text-green-700">£{(b.total_cost || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-center font-mono" data-testid={`dispatch-candidates-${b.ref}`}>{b.candidates_visible}</TableCell>
                      <TableCell className="text-xs text-slate-500"><span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{nextT ? fmtTime(nextT) : "—"}</span></TableCell>
                      <TableCell>
                        {b.dispatch_state !== "national" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs text-blue-600 border-blue-200" onClick={() => releaseNationally(b.booking_id)} disabled={actionLoading} data-testid={`release-national-${b.ref}`}>
                            <Globe2 className="h-3 w-3 mr-1" /> Release
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-slate-400">Current settings: priority {settings.priority_radius_miles}mi for {Math.round((settings.priority_duration_seconds||0)/60)} min → regional {settings.regional_radius_miles}mi for {Math.round((settings.regional_duration_seconds||0)/60)} min → national.</p>

      {/* Phase 10 — Nearby drivers (list + small map) */}
      <NearbyDriversPanel auth={auth} API={API} />
    </div>
  );
}
